import React, { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RoleProvider, useRole } from './context/RoleContext';
import { CrmAccountProvider, useCrmAccount } from './context/CrmAccountContext';
import { ToastProvider, useToast } from './components/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import { supabase } from './lib/supabaseClient';
import {
  IconUsers, IconBriefcase, IconCalendar, IconInvoices,
  IconTarget, IconPackage, IconBell, IconMessage,
  IconPieChart, IconDashboard, IconMapPin
} from './components/Icons';
import GooeyNav from './components/GooeyNav/GooeyNav';
import './App.css';

// Customer Portal imports
import { PortalProvider, PortalErrorBoundary } from './portal/PortalContext';
import PortalLogin from './portal/PortalLogin';
import PortalAuthHandler from './portal/PortalAuthHandler';
import PortalDashboard from './portal/PortalDashboard';
import PortalInvoicesList from './portal/PortalInvoicesList';
import PortalInvoiceDetail from './portal/PortalInvoiceDetail';
import PortalJobs from './portal/PortalJobs';
import PortalServiceRequest from './portal/PortalServiceRequest';

const ALL_NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: <IconDashboard /> },
  { id: 'jobs', label: 'Jobs & Inquiries', icon: <IconBriefcase /> },
  { id: 'schedule', label: 'Schedule', icon: <IconCalendar /> },
  { id: 'tracking', label: 'Map', icon: <IconMapPin /> },
  { id: 'quotes-invoices', label: 'Quotes & Invoices', icon: <IconInvoices /> },
  { id: 'leads-customers', label: 'Leads & Customers', icon: <IconUsers /> },
  { id: 'reports', label: 'Reports', icon: <IconPieChart /> },
  { id: 'ai', label: 'Toby', icon: <IconMessage /> },
  { id: 'notifications', label: 'Notifications', icon: <IconBell /> },
];

// ── Lazy-load every page component ──
const Login = lazy(() => import('./components/Login'));
const LandingPage = lazy(() => import('./components/LandingPage'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const CrmMvpDashboard = lazy(() => import('./components/CrmMvpDashboard'));
const JobsAndInquiries = lazy(() => import('./components/JobsAndInquiries'));
const AIAssistant = lazy(() => import('./components/AIAssistant'));
const Schedule = lazy(() => import('./components/Schedule'));
const QuotesInvoicesPage = lazy(() => import('./components/QuotesInvoicesPage'));
const LeadsCustomersPage = lazy(() => import('./components/LeadsCustomersPage'));
const Reports = lazy(() => import('./components/Reports'));
const Notifications = lazy(() => import('./components/Notifications'));
const NotificationBell = lazy(() => import('./components/NotificationBell'));
const Tracking = lazy(() => import('./components/Tracking'));
const Settings = lazy(() => import('./components/Settings'));
const FunnelPage = lazy(() => import('./components/FunnelPage'));
const BookPage = lazy(() => import('./components/BookPage'));
const SetterApplyPage = lazy(() => import('./components/SetterApplyPage'));

const PageLoader = () => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100%', minHeight: '400px',
    background: '#111827', gap: '16px'
  }}>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <div style={{
      width: 40, height: 40, border: '3px solid #374151',
      borderTopColor: '#10b981', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }} />
    <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>Loading…</p>
  </div>
);

const PAGE_LABELS = {
  dashboard: 'Dashboard', inquiries: 'Inquiries', jobs: 'Jobs', schedule: 'Schedule', tracking: 'Map',
  'quotes-invoices': 'Quotes & Invoices', 'leads-customers': 'Leads & Customers',
  reports: 'Reports', ai: 'Toby', notifications: 'Notifications', settings: 'Settings'
};

const AppContent = () => {
  const { user, logout, loading } = useAuth();
  const { userRole, loading: roleLoading, businessId } = useRole();
  const { isCrmUser, loading: crmAccountLoading } = useCrmAccount();
  const { showToast } = useToast();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [darkMode, setDarkMode] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
const [jobPrefill, setJobPrefill] = useState(null);
  /** { action: 'edit' | 'photos', job } — consumed by JobsAndInquiries when navigating from Dashboard */
  const [jobsNavigateIntent, setJobsNavigateIntent] = useState(null);
  const [invoicePrefill, setInvoicePrefill] = useState(null);
  const [quotePrefill, setQuotePrefill] = useState(null);
  const [aiPrefillMessage, setAiPrefillMessage] = useState(null);
  const [showLoginPage, setShowLoginPage] = useState(false);
  const mainScrollRef = useRef(null);
  const isFullHeightPage = currentPage === 'ai' || currentPage === 'tracking' || currentPage === 'schedule';

  const navItems = useMemo(() => {
    if (userRole === 'technician') {
      return [
        { id: 'dashboard', label: 'My Jobs', icon: <IconBriefcase /> },
        { id: 'jobs', label: 'Jobs', icon: <IconBriefcase /> },
        { id: 'schedule', label: 'My Schedule', icon: <IconCalendar /> },
        { id: 'tracking', label: 'Map', icon: <IconMapPin /> },
        { id: 'materials', label: 'Materials', icon: <IconPackage /> },
        { id: 'ai', label: 'Toby', icon: <IconMessage /> },
      ];
    }
    if (userRole === 'admin') {
      return ALL_NAV_ITEMS.filter((item) => item.id !== 'reports');
    }
    return ALL_NAV_ITEMS;
  }, [userRole]);

  const gooeyItems = useMemo(() =>
    navItems.map((item) => ({
      label: item.label,
      icon: item.icon,
      href: '#',
      onClick: () => { setCurrentPage(item.id); setIsMobileOpen(false); },
    })),
    [navItems]);

  const activeNavIndex = navItems.findIndex((n) => n.id === currentPage);
  const gooeyActiveIndex = activeNavIndex >= 0 ? activeNavIndex : 0;

  useEffect(() => {
    if (navItems.some((n) => n.id === currentPage) || currentPage === 'settings') return;
    setCurrentPage(navItems[0]?.id || 'dashboard');
  }, [currentPage, navItems]);

  useEffect(() => {
    const el = mainScrollRef.current;
    if (el) el.scrollTop = 0;
  }, [currentPage]);

  // Only lock #root to the viewport for the authenticated in-app shell.
  // Public routes (landing/login) need normal document scrolling.
  useEffect(() => {
    const rootEl = document.getElementById('root');
    if (!rootEl) return undefined;
    if (user) rootEl.classList.add('root-viewport-lock');
    else rootEl.classList.remove('root-viewport-lock');
    return () => rootEl.classList.remove('root-viewport-lock');
  }, [user]);

  // For full-height pages (tracking/schedule/ai) and mobile menu open state,
  // lock background/root scrolling so the page cannot scroll past the page bottom.
  useEffect(() => {
    const rootEl = document.getElementById('root');
    if (!rootEl) return undefined;

    const shouldLockRootScroll = isMobileOpen || isFullHeightPage;
    if (!shouldLockRootScroll) return undefined;

    const prevRootOverflow = rootEl.style.overflow;
    const prevRootOverflowY = rootEl.style.overflowY;
    const prevRootMaxHeight = rootEl.style.maxHeight;
    const prevRootHeight = rootEl.style.height;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;

    // `.app-mobile-scale` uses min-height > 100vh; without a hard cap, #root grows and stays scrollable.
    rootEl.style.overflow = 'hidden';
    rootEl.style.overflowY = 'hidden';
    rootEl.style.maxHeight = '100dvh';
    rootEl.style.height = '100dvh';
    rootEl.style.overscrollBehavior = 'none';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.classList.add('app-scroll-lock');

    const sidebarEl = document.querySelector('.mobile-sidebar');
    const shouldAllow = (target) => {
      // On full-height pages with menu closed, block all background gestures.
      if (!isMobileOpen) return false;
      if (!sidebarEl) return false;
      return !!(target && sidebarEl.contains(target));
    };

    const preventBackgroundScroll = (e) => {
      // Allow scrolling the sidebar itself (if it overflows),
      // but block background/root scrolling gestures.
      if (shouldAllow(e.target)) return;
      e.preventDefault();
    };

    window.addEventListener('wheel', preventBackgroundScroll, { passive: false });
    window.addEventListener('touchmove', preventBackgroundScroll, { passive: false });

    return () => {
      rootEl.style.overflow = prevRootOverflow;
      rootEl.style.overflowY = prevRootOverflowY;
      rootEl.style.maxHeight = prevRootMaxHeight;
      rootEl.style.height = prevRootHeight;
      rootEl.style.overscrollBehavior = '';
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.classList.remove('app-scroll-lock');
      window.removeEventListener('wheel', preventBackgroundScroll);
      window.removeEventListener('touchmove', preventBackgroundScroll);
    };
  }, [isMobileOpen, isFullHeightPage]);

  const checkOverdueInvoices = React.useCallback(async () => {
    if (!user?.id) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const { data: invoices, error: invErr } = await supabase
        .from('invoices')
        .select('id, invoice_number, customer_name, amount, due_date')
        .eq('user_id', businessId || user.id)
        .in('status', ['pending', 'sent', 'overdue'])
        .lt('due_date', today);
      if (invErr || !invoices?.length) return;

      const invoiceIds = invoices.map((i) => i.id);
      const { data: existing, error: notifErr } = await supabase
        .from('ai_notifications')
        .select('related_invoice_id')
        .eq('user_id', businessId || user.id)
        .eq('type', 'invoice_overdue')
        .eq('is_dismissed', false)
        .in('related_invoice_id', invoiceIds);
      if (notifErr) return;

      const alreadyNotified = new Set((existing || []).map((n) => n.related_invoice_id));

      for (const inv of invoices) {
        if (alreadyNotified.has(inv.id)) continue;
        const dueDate = new Date(inv.due_date);
        const daysOverdue = Math.floor((new Date() - dueDate) / (1000 * 60 * 60 * 24));
        const severity = daysOverdue >= 15 ? 'critical' : 'warning';
        const amount = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(inv.amount || 0);

        await supabase.from('ai_notifications').insert([{
          user_id: businessId || user.id,
          type: 'invoice_overdue',
          severity,
          title: `Invoice ${inv.invoice_number} is ${daysOverdue} Day${daysOverdue !== 1 ? 's' : ''} Overdue`,
          message: `${inv.customer_name || 'Customer'} owes ${amount} — due ${inv.due_date}`,
          action_url: '/invoices',
          action_label: 'View Invoice',
          related_invoice_id: inv.id,
          is_read: false,
          is_dismissed: false,
        }]);
      }
    } catch (err) {
      console.error('Overdue invoice check failed:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    checkOverdueInvoices();
    const interval = setInterval(checkOverdueInvoices, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user?.id, checkOverdueInvoices]);

  useEffect(() => {
    document.documentElement.dataset.theme = darkMode ? 'dark' : 'light';
  }, [darkMode]);

  const bootLoading = loading || (user && roleLoading) || (user && crmAccountLoading);

  useEffect(() => {
    if (bootLoading || !user || isCrmUser) return;
    const setupErr = typeof window !== 'undefined' ? window.__tradelynxAccountSetupError : '';
    if (!setupErr) return;
    showToast(setupErr, 'error');
    window.__tradelynxAccountSetupError = '';
  }, [user, isCrmUser, bootLoading, showToast]);

  if (bootLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <Suspense fallback={<PageLoader />}>
        {showLoginPage ? (
          <Login onBack={() => setShowLoginPage(false)} />
        ) : (
          <LandingPage onLoginClick={() => setShowLoginPage(true)} />
        )}
      </Suspense>
    );
  }

  // Dedicated CRM-only experience for this user:
  // no TradeLynx sidebar, no legacy pages, no AI widget.
  if (isCrmUser) {
    return (
      <Suspense fallback={<PageLoader />}>
        <CrmMvpDashboard
          onLogout={async () => {
            await logout();
            setShowLoginPage(false);
          }}
        />
      </Suspense>
    );
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard': return (
        <Dashboard
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          onNavigate={setCurrentPage}
          onNavigateToJobsWithIntent={(intent) => {
            setJobsNavigateIntent(intent);
            setCurrentPage('jobs');
          }}
        />
      );
      case 'jobs': return (
        <JobsAndInquiries
          darkMode={darkMode}
          onNavigateToQuotes={(data) => { setQuotePrefill(data || null); setCurrentPage('quotes-invoices'); }}
          jobPrefill={jobPrefill}
          onJobPrefillUsed={() => setJobPrefill(null)}
          onNavigateToInvoices={(data) => { setInvoicePrefill(data || null); setCurrentPage('quotes-invoices'); }}
          jobsNavigateIntent={jobsNavigateIntent}
          onJobsNavigateIntentUsed={() => setJobsNavigateIntent(null)}
        />
      );
      case 'materials': return <JobsAndInquiries darkMode={darkMode} onNavigateToQuotes={(data) => { setQuotePrefill(data || null); setCurrentPage('quotes-invoices'); }} initialTab="materials" />;
      case 'schedule': return <Schedule darkMode={darkMode} onNavigate={setCurrentPage} />;
      case 'tracking': return <Tracking darkMode={darkMode} user={user} businessId={businessId} />;
case 'quotes-invoices': return (
        <QuotesInvoicesPage
          darkMode={darkMode}
          onNavigateToJobs={(data) => { setJobPrefill(data || null); setCurrentPage('jobs'); }}
          invoicePrefill={invoicePrefill}
          onInvoicePrefillUsed={() => setInvoicePrefill(null)}
          onOpenInvoiceWithPrefill={(data) => setInvoicePrefill(data || null)}
          onAskTobyToSend={(message) => { setAiPrefillMessage(message); setCurrentPage('ai'); }}
          quotePrefill={quotePrefill}
          onQuotePrefillUsed={() => setQuotePrefill(null)}
        />
      );
      case 'leads-customers': return <LeadsCustomersPage darkMode={darkMode} />;
      case 'reports': return <Reports darkMode={darkMode} />;
      case 'ai': return <AIAssistant darkMode={darkMode} isPageView={true} prefillMessage={aiPrefillMessage} onPrefillUsed={() => setAiPrefillMessage(null)} />;
      case 'notifications': return <Notifications darkMode={darkMode} />;
      case 'settings': return <Settings darkMode={darkMode} />;
      default: return (
        <Dashboard
          darkMode={darkMode}
          setDarkMode={setDarkMode}
          onNavigate={setCurrentPage}
          onNavigateToJobsWithIntent={(intent) => {
            setJobsNavigateIntent(intent);
            setCurrentPage('jobs');
          }}
        />
      );
    }
  };

  return (
    <div
      className={`flex h-screen min-h-0 max-h-[100dvh] overflow-hidden app-mobile-scale ${darkMode ? 'dark' : ''} ${
        isMobileOpen ? 'menu-open' : ''
      }`}
    >
      {/* Mobile Menu Overlay - rendered outside the scaled container */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[9999] md:hidden"
          style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed inset-y-0 left-0 z-[10000] w-72 max-w-[85vw] flex flex-col border-r overflow-y-auto
        transition-transform duration-300 ease-in-out shadow-2xl
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        md:relative md:inset-auto md:left-auto md:z-50 md:translate-x-0 md:max-w-none md:overflow-visible md:shadow-none
        ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
        mobile-sidebar
      `}>
        <button
          className="md:hidden absolute top-3 right-3 text-gray-400 hover:text-white rounded-lg flex items-center justify-center"
          style={{ minWidth: 44, minHeight: 44 }}
          onClick={() => setIsMobileOpen(false)}
          aria-label="Close menu"
        >
          ✕
        </button>

        <div className={`p-6 ${darkMode ? 'border-gray-700' : 'border-gray-200'} border-b`}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center space-x-3 min-w-0">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #065f46, #10b981)' }}>
                <span className="text-white text-xl font-bold">T</span>
              </div>
              <div className="min-w-0">
                <h1 className={`text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>TradeLynx</h1>
                <p className={`text-[10px] md:text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Quick. Connected. Powerful.</p>
              </div>
            </div>
            <Suspense fallback={null}>
              <NotificationBell
                darkMode={darkMode}
                onNavigate={(url) => {
                  const path = (url || '').split('?')[0] || '';
                  if (path === '/jobs' || path.includes('jobs')) setCurrentPage('jobs');
                  else if (path === '/schedule' || path.includes('schedule')) setCurrentPage('schedule');
                  else if (path === '/map' || path.includes('map')) setCurrentPage('tracking');
                  else if (path === '/quotes' || path === '/invoices' || path.includes('quotes') || path.includes('invoices')) setCurrentPage('quotes-invoices');
                  else if (path === '/leads' || path === '/customers' || path.includes('leads') || path.includes('customers')) setCurrentPage('leads-customers');
                  else setCurrentPage('dashboard');
                }}
              />
            </Suspense>
          </div>
        </div>

        {/* Navigation — GooeyNav */}
        <div className="flex-1 p-3" style={{ overflow: 'visible' }}>
          <GooeyNav
            items={gooeyItems}
            activeIndex={gooeyActiveIndex}
            particleCount={15}
            particleDistances={[90, 10]}
            particleR={100}
            animationTime={600}
            timeVariance={300}
            colors={[1, 2, 3, 1, 2, 3, 1, 4]}
            darkMode={darkMode}
          />
        </div>

        <div className={`p-4 ${darkMode ? 'border-gray-700' : 'border-gray-200'} border-t`}>
          <button
            type="button"
            onClick={() => { setCurrentPage('settings'); setIsMobileOpen(false); }}
            className="w-full flex items-center space-x-3 mb-3 rounded-lg p-2 -m-2 transition-colors text-left hover:opacity-90"
            style={{ minHeight: 44 }}
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #065f46, #10b981)' }}>
              <span className="text-white font-bold">{user.email?.[0]?.toUpperCase()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {user.user_metadata?.name || user.email?.split('@')[0] || 'User'}
              </p>
              <p className={`text-xs truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Settings</p>
            </div>
          </button>
          <button
            onClick={() => { logout(); setShowLoginPage(false); }}
            className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
            style={{ minHeight: 44 }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div
        className="flex flex-1 flex-col min-w-0 min-h-0 relative"
        style={{
          background: (currentPage !== 'ai' && currentPage !== 'tracking' && currentPage !== 'schedule')
            ? (darkMode ? '#111827' : '#ffffff')
            : undefined
        }}
      >
        {/* Static diagonal background for all pages (does not move with scroll) */}
        {!isMobileOpen && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 0,
              pointerEvents: 'none',
              overflow: 'hidden',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '340%',
                height: '260px',
                transform: 'translate(-50%, -50%) rotate(-12deg)',
                transformOrigin: 'center',
                // Single combined gradient (lighter gray base + lighter center)
                background: darkMode
                  ? 'linear-gradient(90deg, rgba(220,230,245,0) 0%, rgba(220,230,245,0.28) 8%, rgba(220,230,245,0.78) 18%, rgba(100,115,135,0.45) 30%, rgba(100,115,135,0.45) 70%, rgba(220,230,245,0.78) 82%, rgba(220,230,245,0.28) 92%, rgba(220,230,245,0) 100%)'
                  : 'linear-gradient(90deg, rgba(240,245,250,0) 0%, rgba(240,245,250,0.14) 8%, rgba(240,245,250,0.42) 18%, rgba(140,155,175,0.32) 30%, rgba(140,155,175,0.32) 70%, rgba(240,245,250,0.42) 82%, rgba(240,245,250,0.14) 92%, rgba(240,245,250,0) 100%)',
                overflow: 'hidden',
                filter: 'blur(80px)',
              }}
            >
            </div>
          </div>
        )}

        <div className={`md:hidden fixed top-0 left-0 right-0 z-[1003] h-16 flex items-center justify-between px-4 border-b shadow-lg ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <button
            onClick={() => setIsMobileOpen(true)}
            className="flex items-center justify-center rounded-xl transition-all active:scale-95 text-white"
            style={{ width: 48, height: 48, background: 'linear-gradient(135deg, #065f46, #10b981)' }}
            aria-label="Open menu"
          >
            <span style={{ fontSize: 28, lineHeight: 1 }}>☰</span>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #065f46, #10b981)' }}>
              <span className="text-white text-sm font-bold">T</span>
            </div>
            <h1 className={`font-bold text-xl tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>TradeLynx</h1>
          </div>
          <Suspense fallback={null}>
            <NotificationBell
              darkMode={darkMode}
              onNavigate={(url) => {
                const path = (url || '').split('?')[0] || '';
                if (path === '/jobs' || path.includes('jobs')) setCurrentPage('jobs');
                else if (path === '/schedule' || path.includes('schedule')) setCurrentPage('schedule');
                else if (path === '/map' || path.includes('map')) setCurrentPage('tracking');
                else if (path === '/quotes' || path === '/invoices' || path.includes('quotes') || path.includes('invoices')) setCurrentPage('quotes-invoices');
                else if (path === '/leads' || path === '/customers' || path.includes('leads') || path.includes('customers')) setCurrentPage('leads-customers');
                else setCurrentPage('dashboard');
              }}
            />
          </Suspense>
        </div>

        <div
          ref={mainScrollRef}
          className={`w-full min-h-0 overflow-x-hidden pt-16 md:pt-0 ${isFullHeightPage ? 'flex-1' : 'flex-1 overflow-y-auto'}`}
          style={{
            position: 'relative',
            zIndex: 1,
            overflowY: isFullHeightPage ? 'hidden' : 'auto',
            WebkitOverflowScrolling: 'touch',
            ...(isFullHeightPage ? { minHeight: 0 } : {})
          }}
        >
          <div
            className="flex flex-col"
            style={{
              flexShrink: 0,
              minHeight: isFullHeightPage ? '100%' : undefined,
              ...(isFullHeightPage ? { position: 'relative', height: '100%' } : {})
            }}
          >
            <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                {renderPage()}
              </Suspense>
            </ErrorBoundary>

          </div>
        </div>
      </div>

      {currentPage !== 'ai' && (
        <Suspense fallback={null}>
          <AIAssistant darkMode={darkMode} prefillMessage={aiPrefillMessage} onPrefillUsed={() => setAiPrefillMessage(null)} />
        </Suspense>
      )}
    </div>
  );
};

// Customer Portal Routes Component with error handling
const PortalRoutes = () => {
  console.log('PortalRoutes rendering - version 2');
  return (
    <PortalErrorBoundary>
      <PortalProvider>
        <PortalRoutesContent />
      </PortalProvider>
    </PortalErrorBoundary>
  );
};

const PortalRoutesContent = () => {
  return (
    <Routes>
      <Route path="login" element={<PortalLogin />} />
      <Route path="auth" element={<PortalAuthHandler />} />
      <Route path="dashboard" element={<PortalDashboard />} />
      <Route path="invoices" element={<PortalInvoicesList />} />
      <Route path="invoices/:id" element={<PortalInvoiceDetail />} />
      <Route path="jobs" element={<PortalJobs />} />
      <Route path="service-request" element={<PortalServiceRequest />} />
      <Route path="/" element={<Navigate to="/portal/dashboard" replace />} />
    </Routes>
  );
};

function App() {
  console.log('App component rendering');
  return (
    <BrowserRouter>
      <AuthProvider>
        <RoleProvider>
          <CrmAccountProvider>
          <ToastProvider>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Standalone Funnel Page - Prioritized */}
                <Route
                  path="/funnel/*"
                  element={<FunnelPage />}
                />

                {/* Customer Portal Routes */}
                <Route
                  path="/portal/*"
                  element={(
                    <div className="portal-route-shell">
                      <PortalRoutes />
                    </div>
                  )}
                />

                {/* Booking confirmation page — Google Ads conversion URL */}
                <Route path="/book" element={<BookPage />} />

                {/* Public application page for appointment setters */}
                <Route path="/setters/:slug?" element={<SetterApplyPage />} />
                <Route path="/setter/:slug?" element={<SetterApplyPage />} />
                <Route path="/closers/:slug?" element={<SetterApplyPage role="closer" />} />
                <Route path="/closer/:slug?" element={<SetterApplyPage role="closer" />} />

                {/* Main App Routes (Authenticated or Landing) */}
                <Route path="*" element={<AppContent />} />
              </Routes>
            </Suspense>
          </ToastProvider>
          </CrmAccountProvider>
        </RoleProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
